import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocFromServer, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { Center, Container, Loader, Text, Modal, Group, Space, Stack, Title } from '@mantine/core';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { BrandCard, StampCard, UserCard } from '../../components';
import styles from './user.module.css';

const calculateStampCards = async (stamps) => {
    const stampCardIds = stamps.reduce((acc, stamp) => {
        if (stamp.claimed === false) {
            const stampCardId = stamp.stampCardId;
            if (!acc[stampCardId]) {
                acc[stampCardId] = {
                    count: 0,
                    stamps: []
                };
            }
            acc[stampCardId].count += 1;
            acc[stampCardId].stamps.push({
                stampedDate: stamp.stampedDate
            });
        }
        return acc;
    }, {});

    let stampCards = [];
    
    await Promise.all(Object.keys(stampCardIds).map(async (stampCardId) => {
        try {
            const stampCardSnapshot = await getDoc(doc(db, 'stampCards', stampCardId));
            const stampCard = stampCardSnapshot.data();
            const brand = stamps.find(stamp => stamp.stampCardId === stampCardId)?.brand;
            const { count, stamps: stampDates } = stampCardIds[stampCardId];

            if (count > stampCard.totalStamps) {
                const totalStamps = stampCard.totalStamps;
                const remainingCount = count - totalStamps;
                stampCards.push(
                    { 
                        ...stampCard, 
                        brand, 
                        stampCardId, 
                        count: totalStamps,
                        stamps: stampDates.slice(0, totalStamps)
                    },
                    { 
                        ...stampCard, 
                        brand, 
                        stampCardId, 
                        count: remainingCount,
                        stamps: stampDates.slice(totalStamps)
                    }
                );
            } else {
                stampCards.push({ 
                    ...stampCard, 
                    brand, 
                    stampCardId, 
                    count,
                    stamps: stampDates
                });
            }
        } catch (error) {
            console.error('Error getting stampCard:', error);
            throw error;
        }
    }));

    return stampCards;
};

export const User = ({ userData, open, setOpen, hoverCard, hoverId }) => {
    const params = useParams();
    const userId = hoverCard ? hoverId : params.id;
    const [user, setUser] = useState(userData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [stampCards, setStampCards] = useState([]);

    const closeModal = () => {
        setModalOpen(false);
    };

    const fetchUserActivity = async (targetUserId) => {
        const [stampsSnapshot, rewardsSnapshot] = await Promise.all([
            getDocs(query(collection(db, 'stamps'), where('userId', '==', targetUserId))),
            getDocs(query(collection(db, 'rewards'), where('userId', '==', targetUserId)))
        ]);

        const stamps = stampsSnapshot.docs.map((stampDoc) => ({
            ...stampDoc.data(),
            id: stampDoc.id
        }));
        const rewards = rewardsSnapshot.docs.map((rewardDoc) => ({
            ...rewardDoc.data(),
            id: rewardDoc.id
        }));
        const brandIds = Array.from(new Set(stamps.map((stamp) => stamp.brandId).filter(Boolean)));
        const brands = await Promise.all(brandIds.map(async (brandId) => {
            const brandSnapshot = await getDocFromServer(doc(db, 'brands', brandId));
            return brandSnapshot.exists()
                ? { ...brandSnapshot.data(), id: brandId }
                : { id: brandId, name: 'Unknown Brand' };
        }));

        const stampsWithBrands = stamps.map((stamp) => ({
            ...stamp,
            brand: brands.find((brand) => brand.id === stamp.brandId)
        }));

        return { stamps: stampsWithBrands, rewards, brands };
    };

    useEffect(() => {
        if (open) {
            setModalOpen(true);
            setUser(userData);
        }
    }, [open, userData]);

    useEffect(() => {
        const fetchUser = async () => {
            setLoading(true);
            setError('');
            try {
                const docRef = doc(db, 'users', userId);
                const docSnap = await getDocFromServer(docRef);

                if (docSnap.exists()) {
                    const activity = await fetchUserActivity(userId);
                    const nextUser = {
                        ...docSnap.data(),
                        id: userId,
                        stamps: activity.stamps,
                        stampsCount: activity.stamps.length,
                        rewards: activity.rewards,
                        rewardsCount: activity.rewards.length,
                        brands: activity.brands,
                        brandsCount: activity.brands.length
                    };
                    setUser(nextUser);

                    const cards = await calculateStampCards(activity.stamps);
                    setStampCards(cards);
                } else {
                    setError('User not found');
                }
            } catch (error) {
                console.error('Error getting document:', error);
                setError('Error getting document');
            } finally {
                setLoading(false);
            }
        };

        if (userId && !open) {
            fetchUser();
        }
    }, [userId, open]);

    useEffect(() => {
        if (open && userData?.stamps) {
            setLoading(true);
            calculateStampCards(userData.stamps)
                .then(cards => {
                    setStampCards(cards);
                    setLoading(false);
                })
                .catch(err => {
                    setError(err.message);
                    setLoading(false);
                });
        }
    }, [open, userData]);

    function mainBody() {
        return (
            <Stack gap="xl" className={styles.content}>
                {/* User Details Section */}
                <UserCard user={user} />

                {/* brands section */}
                {user.brands?.length > 0 &&
                    <Container mt="lg">
                        <Title order={3} ta="center">Brands Visited</Title>
                        <Group justify='center' m={20}>
                            {user.brands?.map((brand) => {
                                return <BrandCard key={brand.id} brand={brand} />;
                            })}
                        </Group>

                        <Space py="lg" />
                    </Container>
                }

                {/* Stamp Cards Section */}
                {stampCards?.length > 0 &&
                    <Container mt="lg">
                        <Title order={3} ta="center">Stamp Cards</Title>
                        <Group justify='center' m={20}>
                            {stampCards.map((stampCard, index) => (
                                <StampCard key={stampCard.stampCardId + index} stampCard={stampCard} />
                            ))}
                        </Group>

                        <Space py="lg" />
                    </Container>
                }
            </Stack>
        );
    }

    return (
        <Container size="xl" className={styles.container}>
            <Helmet>
                <title>User - {user?.name || 'Unknown'}</title>
            </Helmet>
            {loading && (
                <Center py="xl">
                    <Loader />
                </Center>
            )}
            {error && <Text align="center" color="red">{error}</Text>}
            {user && (
                userId ? (
                    mainBody()
                ) : (
                    <Modal
                        fullScreen
                        opened={modalOpen || open}
                        onClose={
                            () => {
                                closeModal();
                                setOpen?.(false);
                            }
                        }
                    >
                        {mainBody()}
                    </Modal>
                )
            )}
        </Container>
    );
};
